import api from '@/api';
import _ from 'lodash';

const OTP_EXPIRY_KEY = 'twofa_otp_expiry';
const OTP_SENT_KEY = 'twofa_otp_sent';
const OTP_DESTINATION_KEY = 'twofa_otp_destination';

// Shares one in-flight request so a refresh and the 2FA screen cannot both email.
let pending2faRequest = null;

function remainingOtpSeconds() {
  const storedExpiry = sessionStorage.getItem(OTP_EXPIRY_KEY);
  if (!storedExpiry) {
    return 0;
  }
  return Math.floor((Number(storedExpiry) - Date.now()) / 1000);
}

const state = {
  address: '',
  attending: null,
  revoked: false,
  code: '',
  customUnitData: {shares: '1'},
  email: '',
  name: '',
  unit: '',
  isVerified: false,
  multiConsent: null,
  communicationPreferences: null
};

const getters = {
  voter: state => state,
  isVerified: state => state.isVerified,
  isAttending: state => state.isVerified && state.attending,

  // If unit has an ineligible_reason flag or shares=0,
  // voting is disabled for this voter
  isVotingDisabled: state => {
    // First, check for ineligible_reason flag
    if (
      state.customUnitData &&
      'ineligible_reason' in state.customUnitData &&
      state.customUnitData.ineligible_reason !== ''
    ) {
      return true;
    }
    // Otherwise: default case, no shares means default share of 1
    if (!state.customUnitData || !('shares' in state.customUnitData)) {
      return false;
    }
    // If shares is not a number, then sometimes is wrong and just disable
    if (isNaN(state.customUnitData.shares)) {
      return true;
    }
    // Otherwise check that shares is > 0
    return parseFloat(state.customUnitData.shares) <= 0;
  },
  noVoterMultiConsent: (state, getters, rootState) => {
    // Note: Must allow state, getters and rootState params
    // Check consent format & voter consent & test voter status
    const consentFormat = _.get(rootState, 'meetingOptions.consent_format');
    const votingConsent = _.get(state, 'multiConsent.voting_consent', false);
    const isTestVoter = _.get(state, 'communicationPreferences.isTestVoter');

    // Bypass voting consent check if test voter
    if (isTestVoter) {
      return false;
    }

    return consentFormat === 'multi-consent' && !votingConsent;
  }
};

const actions = {
  setVoter({commit}, voter) {
    return commit('SET_VOTER', voter);
  },

  /**
   * Marks the owner as attending the meeting
   * @param {String} shortCode - the shortCode
   * @param {String} token     - the voter Token
   */
  async markRsvp({commit}, {shortCode, token, isAttending}) {
    try {
      await api.postVoterAttending(shortCode, token, isAttending);
      return commit('SET_ATTENDING', true);
    } catch (err) {
      throw err;
    }
  },

  /**
   * Self-revokes submissions
   * @param {String} shortCode - the shortCode
   * @param {String} token     - the voter Token
   */

  async revokeSubmissions({commit}, {shortCode, token}) {
    try {
      const res = await api.postRevokeSubmissions(shortCode, token);
      commit('SET_REVOKED', true);
      return res;
    } catch (err) {
      throw err;
    }
  },

  clearVoter({commit}) {
    return commit('SET_VOTER', {
      address: '',
      attending: null,
      code: '',
      customUnitData: {},
      email: '',
      name: '',
      unit: '',
      isVerified: false,
      communicationPreferences: null
    });
  },

  /**
   * Send a 2FA OTP to the voter's registered email.
   * Uses rootState for shortCode/token (they live in the root store).
   *
   * Skips the email when a code from this tab is still inside its TTL.
   * Pass {force: true} for an explicit resend.
   */
  async request2fa({rootState, commit}, payload) {
    const force = Boolean(payload && payload.force);
    const remaining = remainingOtpSeconds();

    if (!force && remaining > 0) {
      const destination = sessionStorage.getItem(OTP_DESTINATION_KEY) || '';
      if (destination) {
        commit('SET_TWO_FA_MASKED_EMAIL', destination, {root: true});
      }
      return {ttl: remaining, destination: destination, reused: true};
    }

    if (!force && pending2faRequest) {
      return pending2faRequest;
    }

    const request = api
      .request2fa(rootState.shortCode, rootState.token)
      .then(function({data}) {
        commit('SET_TWO_FA_MASKED_EMAIL', data.destination, {root: true});
        const ttl = (data && data.ttl) || 300;
        sessionStorage.setItem(OTP_SENT_KEY, '1');
        sessionStorage.setItem(
          OTP_EXPIRY_KEY,
          String(Date.now() + ttl * 1000)
        );
        if (data && data.destination) {
          sessionStorage.setItem(OTP_DESTINATION_KEY, data.destination);
        }
        return data;
      });

    if (!force) {
      pending2faRequest = request;
    }

    try {
      return await request;
    } finally {
      if (pending2faRequest === request) {
        pending2faRequest = null;
      }
    }
  },

  /**
   * Verify the OTP code and exchange the invite JWT for an authorized token.
   *
   * NOTE: twoFaRequired is NOT reset here — doing so queues a Vue watcher flush
   * that destroys the TwoFactorAuth component before $emit('verified') fires.
   * App.vue's onVerified handler resets the flag instead.
   */
  async verify2fa({rootState, commit}, {code}) {
    const {data} = await api.verify2fa(rootState.shortCode, rootState.token, code);
    sessionStorage.setItem('token', data.token);
    commit('SET_TOKEN', data.token, {root: true});
    return data;
  }
};

const mutations = {
  /**
   * Set the attending flag
   * @param {Object} state        the state
   * @param {Object} voter        the voter object
   */
  SET_VOTER(state, voter) {
    state.address = voter.address;
    state.attending = voter.attending;
    state.code = voter.code;
    state.customUnitData = voter.customUnitData;
    state.email = voter.email;
    state.name = voter.name;
    state.unit = voter.unit;
    state.isVerified = voter.isVerified;
    state.multiConsent = voter.multiConsent;
    state.communicationPreferences = voter.communicationPreferences;
  },

  /**
   * Set the attending flag
   * @param {Object} state        the state
   * @param {Boolean} attending   set the attending value
   */
  SET_ATTENDING(state, attending) {
    state.attending = attending;
  },

  /**
   * Set the revocation flag
   * @param {Object} state        the state
   * @param {Boolean} attending   set the revocation value
   */

  SET_REVOKED(state, revoked) {
    state.revoked = revoked;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
